import { useState, useEffect } from 'react';
import { responseMethods, preventionGuide, notices } from '@/mocks/homeData';
import { Link } from 'react-router-dom';
import supabase from '@/lib/supabase';

interface LatestPost {
  id: number;
  category: string;
  title: string;
  author: string;
  time: string;
}

function formatRelativeTime(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return '방금 전';
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;

    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  } catch {
    return '';
  }
}

export default function ContentGridSection() {
  const [latestPosts, setLatestPosts] = useState<LatestPost[]>([]);

  useEffect(() => {
    fetchLatestPosts();
  }, []);

  const fetchLatestPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('id, category, title, author, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      if (data && data.length > 0) {
        const formatted: LatestPost[] = data.map((p: Record<string, unknown>) => ({
          id: p.id as number,
          category: (p.category as string) || '커뮤니티',
          title: p.title as string,
          author: (p.author as string) || '익명',
          time: formatRelativeTime(p.created_at as string),
        }));
        setLatestPosts(formatted);
      }
    } catch {
      // keep mock data as fallback (default state stays empty and we use mock below)
    }
  };

  const displayPosts = latestPosts.length > 0 ? latestPosts : [];

  return (
    <section className="py-12 md:py-16 bg-background-50" id="prevention">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* 피해 상황별 대응 방법 */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-foreground-950">피해 상황별 대응 방법</h3>
              <Link to="/board" className="text-xs text-primary-500 hover:text-primary-600">
                더보기
              </Link>
            </div>
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <ul className="space-y-3">
                {responseMethods.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-primary-500 mt-0.5" />
                    <span className="text-xs text-foreground-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/report" className="mt-4 text-xs text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1">
                대응 절차 자세히 보기
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          {/* 예방 가이드 */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-foreground-950">예방 가이드</h3>
              <Link to="/search" className="text-xs text-primary-500 hover:text-primary-600">
                더보기
              </Link>
            </div>
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <ul className="space-y-3">
                {preventionGuide.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-primary-500 mt-0.5" />
                    <span className="text-xs text-foreground-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 최신 공지사항 */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-foreground-950">최신 공지사항</h3>
              <Link to="/board" className="text-xs text-primary-500 hover:text-primary-600">
                더보기
              </Link>
            </div>
            <div className="bg-white rounded-lg border border-background-200 p-4">
              <ul className="space-y-3">
                {notices.map((item) => (
                  <li key={item.title} className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 bg-primary-100 text-primary-700 text-[10px] font-bold rounded flex-shrink-0">
                      {item.category}
                    </span>
                    <Link to="/board" className="text-xs text-foreground-700 leading-relaxed truncate hover:text-primary-500 transition-colors">
                      {item.title}
                    </Link>
                    <span className="text-[10px] text-foreground-400 flex-shrink-0 ml-auto">
                      {item.date}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 커뮤니티 최신 글 (Supabase 실시간) */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-foreground-950">커뮤니티 최신 글</h3>
              <Link to="/board" className="text-xs text-primary-500 hover:text-primary-600">
                더보기
              </Link>
            </div>
            <div className="bg-white rounded-lg border border-background-200 p-4">
              {displayPosts.length > 0 ? (
                <ul className="space-y-3">
                  {displayPosts.map((item) => (
                    <li key={item.id} className="flex items-start gap-2">
                      <span className="px-1.5 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] font-bold rounded flex-shrink-0">
                        {item.category}
                      </span>
                      <div className="min-w-0 flex-1">
                        <Link to={`/board/${item.id}`} className="text-xs text-foreground-700 leading-relaxed block truncate hover:text-primary-500 transition-colors">
                          {item.title}
                        </Link>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-foreground-400">{item.author}</span>
                          <span className="text-[10px] text-foreground-400">{item.time}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] font-bold rounded flex-shrink-0">
                      커뮤니티
                    </span>
                    <div className="min-w-0 flex-1">
                      <Link to="/board" className="text-xs text-foreground-700 leading-relaxed block truncate hover:text-primary-500 transition-colors">
                        첫 게시글을 작성해보세요!
                      </Link>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-foreground-400">스카연</span>
                        <span className="text-[10px] text-foreground-400">방금 전</span>
                      </div>
                    </div>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}